import { ThemeName } from "@/components/v3/theme";

type MemberTheme = Exclude<ThemeName, "white">;

export const teamMembers = [
  {
    name: "Alexander Rodriguez",
    slug: "Alexander",
    title: "Counsellor",
    href: "/about",
    image: "/images/team/alex.png",
    video: "/images/team/Alex_Intro.mp4",
    description:
      "As a dedicated Counsellor and Life Coach, my mission is to help individuals reclaim control of their lives and become the best versions of themselves. With a unique background that blends the structured, problem-solving mindset of engineering with an unwavering passion for helping people, I bring a solutions-focused approach to my practice that delivers real, tangible results.",
    linkedin:
      "https://www.linkedin.com/in/alexander-rodriguez-7b551893?originalSubdomain=au",
    email: "info@alexrodriguez.com.au",
    theme: "blue" as MemberTheme,
  },
  {
    name: "Sandy",
    slug: "sandy",
    href: "/teams/sandy",
    title: "Occupational Therapist",
    image: "/images/team/sandy.jpg",
    video: "/images/team/Sandy-hi.mp4",
    description:
      "Hi, I'm Sandy! As an Occupational Therapist, my heart is in helping people of all ages and backgrounds build confidence, gain independence, and thrive in the activities that bring true meaning to their lives. I believe that therapy shouldn't feel like a rigid, one-size-fits-all process. Instead, it works best when it is deeply human, genuinely collaborative, and tailored to the unique rhythms of your day-to-day life.",
    theme: "red" as MemberTheme,
  },
  {
    name: "Tayyaba",
    slug: "tayyaba",
    href: "/teams/tayyaba",
    title: "Psychologist",
    image: "/images/team/tayyaba.png",
    video: "/images/team/Tayyaba_Intro.mp4",
    description:
      "Hi, I'm Tayyaba! As a psychologist fluent in both English and Urdu, my main goal is to make therapy feel comfortable, accessible, and genuinely helpful. I know that reaching out for support can feel like a daunting step, so I focus on creating a warm, judgment-free space where you can feel fully seen and heard from day one.",
    theme: "red" as MemberTheme,
  },
  // {
  //   name: "Shevera Gunasekera",
  //   slug: "shevera",
  //   href: "/teams/shevera",
  //   title: "Counsellor",
  //   image: "/images/team/shevera.png",
  //   video: "/images/team/Shevera_Intro.mp4",
  //   description:
  //     "With a compassionate heart and a wealth of experience, Shevera is dedicated to helping individuals thrive at every stage of life. Having walked alongside clients ranging from age 5 to 93, she creates a safe, welcoming space tailored to each person's unique journey. Whether working with school children, teenagers, adults, or the elderly, Shevera's approach is always strengths-based, individualised, and deeply empowering.",
  //   theme: "purple" as MemberTheme,
  // },
];

export interface AboutContent {
  name: string;
  title: string;
  subtitle: string;
  image: string;
  video?: string;
  paragraphs: string[];
  highlight: string;
}

export interface ExpertiseItem {
  title: string;
  desc: string;
}

export interface FunFact {
  icon: string;
  iconName:
  | "BookIcon"
  | "RunningIcon"
  | "ChartBarIcon"
  | "PawHeartIcon"
  | "CircleCheckIcon";
  title: string;
  description: string;
}

export interface FunFactsContent {
  title: string;
  subtitle: string;
  topRow: FunFact[];
  bottomRow: FunFact[];
}

export interface TeamMemberContent {
  slug: string;
  theme?: "red" | "blue" | "yellow";
  about: AboutContent;
  expertise: {
    title: string;
    subtitle: string;
    items: ExpertiseItem[];
  };
  funFacts: FunFactsContent;
}

// Array of all team member content - add new members here
export const teamMembersContent: TeamMemberContent[] = [
  {
    slug: "sandy",
    theme: "red",
    about: {
      name: "Sandy",
      title: "About Sandy",
      subtitle: "Occupational Therapist",
      image: "/images/team/sandy.jpg",
      video: "/images/team/Sandy-hi.mp4",
      paragraphs: [
        "Hi, I'm Sandy! As an Occupational Therapist, my heart is in helping people of all ages and backgrounds build confidence, gain independence, and thrive in the activities that bring true meaning to their lives. I believe that therapy shouldn't feel like a rigid, one-size-fits-all process. Instead, it works best when it is deeply human, genuinely collaborative, and tailored to the unique rhythms of your day-to-day life.",
        "Throughout my career, I've had the privilege of working across the entire lifespan—supporting young children developing foundational skills, adolescents navigating growth and routine, adults working through rehabilitation or life transitions, and seniors aiming to age comfortably in place. Whether we connect in a clinical setting, your home, a workplace, or out in the community, I focus on understanding your real-world environment, your personal routines, and the support networks around you.",
        "I encompass the full spectrum of occupational therapy services designed to support every stage of your journey. I specialise in comprehensive functional capacity assessments, prescribing assistive technology, recommending home modifications, and coordinating equipment trials to enhance mobility and safety. Beyond practical tools, I work closely with individuals on daily living skills, physical and neurological rehabilitation, sensory processing support, and independent living strategies across diverse healthcare and community frameworks.",
        "I bring a warm, patient, and endlessly optimistic energy to every session. I know that navigating health or lifestyle challenges can feel overwhelming, so my priority is to create a welcoming space where you feel genuinely heard, valued, and respected. Rather than focusing solely on limitations, I take a strengths-based approach—spotlighting what you can do and finding creative solutions to help you move forward.",
        "Ultimately, my mission is to stand alongside you as your advocate, partner, and biggest cheerleader. Together, we'll break down barriers, celebrate every milestone, and make daily living brighter and full of possibility!",
      ],
      highlight: "Your advocate, partner, and biggest cheerleader every step of the way",
    },
    expertise: {
      title: "Areas of Expertise",
      subtitle:
        "Sandy focuses on practical, person-centred occupational therapy that builds independence and confidence across every life stage.",
      items: [
        {
          title: "Functional Capacity Assessments",
          desc: "Comprehensive assessments that reflect real-world abilities, challenges, strengths, and goals.",
        },
        {
          title: "Assistive Technology & Home Modifications",
          desc: "Prescribing assistive technology, recommending home modifications, and coordinating equipment trials to enhance mobility and safety.",
        },
        {
          title: "Rehabilitation & Independent Living",
          desc: "Support with physical and neurological rehabilitation, sensory processing, and strategies for independent living.",
        },
        {
          title: "Lifespan Care",
          desc: "Working across the full lifespan—children, adolescents, adults, and seniors—in clinical, home, workplace, and community settings.",
        },
      ],
    },
    funFacts: {
      title: "Fun Facts About Sandy",
      subtitle: "Little things that show the person behind the practice.",
      topRow: [
        {
          icon: "/icons/running 1.svg",
          iconName: "RunningIcon",
          title: "Avid Concert Goer",
          description:
            "I will almost always have a concert coming up, and I love having live music to look forward to.",
        },
        {
          icon: "/icons/chart-bar-33 1.svg",
          iconName: "ChartBarIcon",
          title: "Glass-Half-Full",
          description:
            "I naturally try to find the positive in situations and believe that there is usually a silver lining, even when things don't go according to plan.",
        },
        {
          icon: "/icons/paw-heart 1.svg",
          iconName: "PawHeartIcon",
          title: "Definitely a Cat Person",
          description:
            "I have two cats who keep me entertained, and I'm always happy to show you a photo or tell you a funny story about them.",
        },
      ],
      bottomRow: [
        {
          icon: "/icons/book.svg",
          iconName: "BookIcon",
          title: "Loves Travelling",
          description:
            "I will almost always have a trip or holiday planned, or at the very least, be thinking about where I want to go next!",
        },
        {
          icon: "/icons/chart-bar-33 1.svg",
          iconName: "ChartBarIcon",
          title: "Problem Solver",
          description:
            "If something needs fixing, I'll always try to figure it out, and if I can't, it will probably stay on my mind until I eventually work out a solution!",
        },
      ],
    },
  },
  {
    slug: "tayyaba",
    theme: "red",
    about: {
      name: "Tayyaba",
      title: "About Tayyaba",
      subtitle: "Psychologist",
      image: "/images/team/tayyaba.png",
      video: "/images/team/Tayyaba_Intro.mp4",
      paragraphs: [
        "Hi, I'm Tayyaba! As a psychologist fluent in both English and Urdu, my main goal is to make therapy feel comfortable, accessible, and genuinely helpful. I know that reaching out for support can feel like a daunting step, so I focus on creating a warm, judgment-free space where you can feel fully seen and heard from day one.",
        "Holding a Master of Professional Psychology, my approach is grounded in real science and evidence-based care. But while the methods behind my work are clinical, the way we use them is entirely practical and down to earth. I don't believe in one-size-fits-all solutions. Instead, I work collaboratively with you to understand your unique story, help you navigate life's ups and downs, and build actionable tools that actually fit into your everyday life.",
        "Whether you're working through a major life transition, managing stress, or simply trying to get back to feeling like yourself, I'm here to walk alongside you.",
        "Outside of the practice, I love taking time to recharge. You'll usually find me outdoors getting my hands dirty in my ornamental garden or spending quality time relaxing and catching up with my family.",
      ],
      highlight:
        "Creating a warm, judgment-free space where you feel fully seen and heard.",
    },
    expertise: {
      title: "Areas of Expertise",
      subtitle:
        "Tayyaba focuses on practical, evidence-based psychology that meets you where you are.",
      items: [
        {
          title: "Evidence-Based Psychology",
          desc: "Grounded in a Master of Professional Psychology, Tayyaba draws on real science and evidence-based care—applied in a practical, down-to-earth way.",
        },
        {
          title: "Life Transitions",
          desc: "Support for major life changes, helping you navigate uncertainty and build confidence as you move forward.",
        },
        {
          title: "Stress & Wellbeing",
          desc: "Practical tools to manage stress, restore balance, and get back to feeling like yourself.",
        },
        {
          title: "Bilingual Support (English & Urdu)",
          desc: "Therapy in English or Urdu, making support accessible and comfortable for clients from diverse backgrounds.",
        },
      ],
    },
    funFacts: {
      title: "Fun Facts About Tayyaba",
      subtitle: "Little things that show the person behind the practice.",
      topRow: [
        {
          icon: "/icons/book.svg",
          iconName: "BookIcon",
          title: "Green Thumb",
          description:
            "I'm a total plant enthusiast! In my spare time, you'll find me out in the yard cultivating bright Dahlias and training climbing plants.",
        },
        {
          icon: "/icons/running 1.svg",
          iconName: "RunningIcon",
          title: "Traveler at Heart",
          description:
            "I love exploring new places and soaking in fresh scenery—most recently on an unforgettable cruise around New Zealand.",
        },
        {
          icon: "/icons/chart-bar-33 1.svg",
          iconName: "ChartBarIcon",
          title: "Foodie at Heart",
          description:
            "I'm always on the hunt for delicious new restaurants. If you put Chinese or Thai food in front of me, I'm a happy person!",
        },
      ],
      bottomRow: [
        {
          icon: "/icons/running 1.svg",
          iconName: "RunningIcon",
          title: "On the Move",
          description:
            "When I'm not in the clinic, most of my energy goes toward keeping up with my two wonderful (and fast-growing!) kids.",
        },
        {
          icon: "/icons/book.svg",
          iconName: "BookIcon",
          title: "Cultural Bridge",
          description:
            "Being fluent in both English and Urdu isn't just part of my work—it fuels my love for connecting with people from all walks of life, whether over a shared meal, a travel story, or a cozy chat.",
        },
      ],
    },
  },
  // {
  //   slug: "shevera",
  //   theme: "yellow",
  //   about: {
  //     name: "Shevera",
  //     title: "About Shevera",
  //     subtitle: "Counsellor",
  //     image: "/images/team/shevera.png",
  //     video: "/images/team/Shevera_Intro.mp4",
  //     paragraphs: [
  //       "Shevera Gunasekera is a Counsellor with a compassionate heart and a wealth of experience. Having walked alongside clients ranging from age 5 to 93, she creates a safe, welcoming space tailored to each person's unique journey. Whether working with school children, teenagers, adults, or the elderly, Shevera's approach is always strengths-based, individualised, and deeply empowering.",
  //       "Shevera holds a Bachelor of Psychology and a Master of Psychotherapy and Counselling from Western Sydney University. Her diverse clinical background spans schools, youth centres, and aged care facilities, with specialised expertise in supporting neurodiverse individuals and culturally diverse communities.",
  //       "Grounded in evidence-based care, Shevera integrates a wide range of therapies — including CBT, ACT, mindfulness, and play therapy — to support clients navigating trauma, anxiety, depression, relationship challenges, and PTSD.",
  //       "Her lifelong commitment to helping others is woven into everything she does. A former State Council member for St John Ambulance NSW and recipient of the prestigious Pride of Australia Medal, Shevera brings an enthusiastic, encouraging, and truly empathetic energy to every session, partnering with you to unlock your full potential.",
  //     ],
  //     highlight: "Partnering with you to unlock your full potential.",
  //   },
  //   expertise: {
  //     title: "Areas of Expertise",
  //     subtitle:
  //       "Shevera brings evidence-based, strengths-focused support across the full lifespan — from children to older adults.",
  //     items: [
  //       {
  //         title: "ADHD Coaching",
  //         desc: "Specialised coaching for neurodiverse individuals, building practical strategies for focus, organisation, and everyday life.",
  //       },
  //       {
  //         title: "Trauma, Anxiety & Depression",
  //         desc: "Evidence-based support for clients navigating trauma, PTSD, anxiety, depression, and relationship challenges.",
  //       },
  //       {
  //         title: "CBT, ACT & Mindfulness",
  //         desc: "Integration of Cognitive Behavioural Therapy, Acceptance and Commitment Therapy, play therapy, and mindfulness to suit each person's needs.",
  //       },
  //       {
  //         title: "Lifespan & Cultural Diversity",
  //         desc: "Experienced working with clients aged 5 to 93 across schools, youth centres, and aged care, with deep cultural competency.",
  //       },
  //     ],
  //   },
  //   funFacts: {
  //     title: "Fun Facts About Shevera",
  //     subtitle: "A glimpse into the person behind the practice.",
  //     topRow: [
  //       {
  //         icon: "/icons/chart-bar-33 1.svg",
  //         iconName: "CircleCheckIcon",
  //         title: "Pride of Australia Medal",
  //         description:
  //           "Back in 2006, I was honored to receive the Pride of Australia Young Aussie Medal from The Daily Telegraph. It’s a milestone that still holds a very special place in my heart today.",
  //       },
  //       {
  //         icon: "/icons/chart-bar-33 1.svg",
  //         iconName: "ChartBarIcon",
  //         title: "Toastmasters Enthusiast",
  //         description:
  //           "Beyond the office, I thrive on helping others find their voice. I’m currently serving as an Area Director, where I get a front-row seat to watching people grow and shine.",
  //       },
  //       {
  //         icon: "/icons/book.svg",
  //         iconName: "BookIcon",
  //         title: "Creative Spark",
  //         description:
  //           "When I’m not working, I love letting my imagination run wild. I’m always dabbling in new projects, from the intricate knots of macramé and air-dry clay sculpting to complex LEGO builds.",
  //       },
  //     ],
  //     bottomRow: [
  //       {
  //         icon: "/icons/book.svg",
  //         iconName: "BookIcon",
  //         title: "Always Growing",
  //         description:
  //           "I am a self-proclaimed lifelong learner. The best part? My clients are my greatest teachers, I pick up something new and valuable from every single one of them.",
  //       },
  //       {
  //         icon: "/icons/paw-heart 1.svg",
  //         iconName: "PawHeartIcon",
  //         title: "A World of Perspective",
  //         description:
  //           "Born in Hong Kong to Sri Lankan parents and raised in Australia, my life has been a vibrant mix of cultures. This multicultural background is my secret sauce for connecting deeply with diverse clients.",
  //       },
  //     ],
  //   },
  // },
  // Add more team members here
];
